import React, { useEffect } from 'react';
import { useLocation, Link, Outlet, useNavigate } from 'react-router-dom';
import { 
  Home, ClipboardList, CheckSquare, FileText, AlertTriangle, 
  Shield, ClipboardCheck, Settings, Package, FileBox, 
  BarChart3, FolderArchive, Link2, Users, ChevronDown,
  Eye, Scan, Building2, Calendar, Clock, Menu, X
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useTrace } from '../../context/TraceContext';
import { useFilters } from '../../context/FilterContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent } from '../ui/sheet';
import { cn } from '../../lib/utils';
import TraceOverlay from '../trace/TraceOverlay';
import TraceInspector from '../trace/TraceInspector';
import { plants, getAreasByPlant } from '../../data/mockData';

// Navigation items with role-based access
const navItems = [
  { path: '/', icon: Home, label: 'Dashboard', badge: null, roles: 'all' },
  { path: '/tasks', icon: ClipboardList, label: 'Tasks', badge: '7', roles: ['QA_HEAD', 'QA_OFFICER', 'PROD_SUP', 'QC_ANALYST', 'ENGINEERING', 'OPERATOR', 'WAREHOUSE', 'EXT_AUDITOR'] },
  { 
    path: '/checklists', 
    icon: CheckSquare, 
    label: 'Checklists',
    roles: ['QA_HEAD', 'QA_OFFICER', 'PROD_SUP', 'QC_ANALYST', 'ENGINEERING', 'OPERATOR', 'WAREHOUSE', 'EXT_AUDITOR'],
    children: [
      { path: '/checklists/builder', label: 'Builder' },
      { path: '/checklists/library', label: 'Library' },
      { path: '/checklists/executions', label: 'Executions' }
    ]
  },
  { path: '/requirements', icon: FileText, label: 'Requirements', badge: null, roles: ['QA_HEAD', 'QA_OFFICER', 'INT_AUDITOR', 'EXT_AUDITOR', 'CORPORATE'] },
  { 
    path: '/quality-events', 
    icon: AlertTriangle, 
    label: 'Quality Events',
    roles: ['QA_HEAD', 'QA_OFFICER', 'PROD_SUP', 'QC_ANALYST', 'ENGINEERING', 'INT_AUDITOR', 'EXT_AUDITOR'],
    children: [
      { path: '/deviations', label: 'Deviations', badge: '14' },
      { path: '/capa', label: 'CAPA', badge: '5' },
      { path: '/change-control', label: 'Change Control', badge: '3' },
      { path: '/complaints', label: 'Complaints & Recalls' }
    ]
  },
  { 
    path: '/audits', 
    icon: ClipboardCheck, 
    label: 'Audits',
    roles: ['QA_HEAD', 'QA_OFFICER', 'INT_AUDITOR', 'EXT_AUDITOR', 'CORPORATE'],
    children: [
      { path: '/audits/self-inspections', label: 'Self-Inspections' },
      { path: '/audits/supplier', label: 'Supplier Audits' }
    ]
  },
  { path: '/assets', icon: Settings, label: 'Assets & Utilities', badge: null, roles: ['QA_HEAD', 'QA_OFFICER', 'PROD_SUP', 'QC_ANALYST', 'ENGINEERING', 'EXT_AUDITOR'] },
  { path: '/materials', icon: Package, label: 'Materials & Suppliers', badge: null, roles: ['QA_HEAD', 'QA_OFFICER', 'QC_ANALYST', 'WAREHOUSE', 'EXT_AUDITOR'] },
  { path: '/documents', icon: FileBox, label: 'Documents & Training', badge: null, roles: ['QA_HEAD', 'QA_OFFICER', 'PROD_SUP', 'QC_ANALYST', 'ENGINEERING', 'INT_AUDITOR', 'EXT_AUDITOR'] },
  { path: '/pqr', icon: BarChart3, label: 'PQR & Management', badge: null, roles: ['QA_HEAD', 'QA_OFFICER', 'INT_AUDITOR', 'EXT_AUDITOR', 'CORPORATE'] },
  { path: '/audit-packs', icon: FolderArchive, label: 'Audit Packs', badge: null, roles: ['QA_HEAD', 'QA_OFFICER', 'INT_AUDITOR', 'EXT_AUDITOR', 'CORPORATE'] },
  { path: '/integrations', icon: Link2, label: 'Integrations', badge: '1', roles: ['QA_HEAD', 'ENGINEERING', 'EXT_AUDITOR'] },
  { path: '/admin', icon: Users, label: 'Admin', badge: null, roles: ['QA_HEAD'] }
];

const Shell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentRole, switchRole, roles, canEdit, isReadOnly, shouldRedirect, setShouldRedirect } = useApp();
  const { pidTraceMode, toggleTraceMode, updateRouteTrace } = useTrace();
  const { plant, area, timeRange, setPlant, setArea, setTimeRange } = useFilters();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [expandedItems, setExpandedItems] = React.useState(['/checklists', '/quality-events', '/audits']);

  // Get areas for currently selected plant
  const plantAreas = getAreasByPlant(plant);

  useEffect(() => {
    updateRouteTrace(location.pathname);
  }, [location.pathname, updateRouteTrace]);

  // Handle role switch redirect
  useEffect(() => {
    if (shouldRedirect) {
      navigate('/');
      setShouldRedirect(false);
    }
  }, [shouldRedirect, navigate, setShouldRedirect]);

  const toggleExpanded = (path) => {
    setExpandedItems(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Check if current role can access a nav item
  const canAccessNavItem = (item) => {
    if (item.roles === 'all') return true;
    if (currentRole.id === 'EXT_AUDITOR') return true; // External auditor can view all but read-only
    return item.roles.includes(currentRole.id);
  };

  // Filter nav items based on role
  const filteredNavItems = navItems.filter(canAccessNavItem);

  const NavItem = ({ item, depth = 0 }) => {
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.path);
    const active = isActive(item.path);

    if (hasChildren) {
      return (
        <div>
          <button
            onClick={() => toggleExpanded(item.path)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all",
              active ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            )}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" />}
            <span className="flex-1 text-left font-medium">{item.label}</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")} />
          </button>
          {isExpanded && (
            <div className="ml-7 mt-1 space-y-1 border-l border-slate-200 pl-3">
              {item.children.map(child => (
                <Link
                  key={child.path}
                  to={child.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all",
                    isActive(child.path) 
                      ? "bg-[#fd4816]/10 text-[#fd4816] font-medium" 
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  )}
                >
                  <span>{child.label}</span>
                  {child.badge && (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                      {child.badge}
                    </Badge>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all",
          active 
            ? "bg-[#fd4816]/10 text-[#fd4816] border-l-2 border-[#fd4816] -ml-[2px] pl-[14px] font-medium" 
            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        )}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <span className="flex-1 font-medium">{item.label}</span>
        {item.badge && (
          <Badge variant="outline" className="bg-[#fd4816]/10 text-[#fd4816] border-[#fd4816]/30 text-xs">
            {item.badge}
          </Badge>
        )}
      </Link>
    );
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-slate-200">
        <Link to="/" className="flex items-end gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_pharma-gxp/artifacts/zao2fjhi_swishx-updated-orng-logo%20%281%29.png" 
            alt="SwishX" 
            className="h-7 w-auto"
          />
          <span className="font-bold leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: '1.5rem', color: '#fd4816', letterSpacing: 'normal' }}>
            sQMS
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {filteredNavItems.map(item => (
            <NavItem key={item.path} item={item} />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 text-xs text-slate-500">
        <p>Schedule-M Compliance Platform</p>
        <p>v1.1.2 • © 2026 sQMS</p>
      </div>
    </>
  );

  return (
    <div className={cn("min-h-screen bg-slate-50 text-slate-900", pidTraceMode && "trace-mode-active")}>
      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-white border-slate-200">
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-white border-r border-slate-200 hidden lg:flex flex-col shadow-sm">
        <SidebarContent />
      </aside>

      {/* Main Content Area */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between h-full px-4 gap-4">
            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-600"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Selectors */}
            <div className="flex items-center gap-2 flex-1">
              {/* Plant Selector */}
              <Select value={plant} onValueChange={setPlant}>
                <SelectTrigger className="w-[180px] bg-white border-slate-200 text-slate-700 h-9" data-testid="plant-selector">
                  <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {plants.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-slate-700 focus:bg-slate-100">
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Area Selector */}
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger className="w-[180px] bg-white border-slate-200 text-slate-700 h-9 hidden md:flex" data-testid="area-selector">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all" className="text-slate-700 focus:bg-slate-100">
                    All Areas
                  </SelectItem>
                  {plantAreas.map(a => (
                    <SelectItem key={a.id} value={a.id} className="text-slate-700 focus:bg-slate-100">
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Time Scope */}
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-[140px] bg-white border-slate-200 text-slate-700 h-9 hidden lg:flex" data-testid="time-selector">
                  <Clock className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="7days" className="text-slate-700 focus:bg-slate-100">Last 7 days</SelectItem>
                  <SelectItem value="30days" className="text-slate-700 focus:bg-slate-100">Last 30 days</SelectItem>
                  <SelectItem value="quarter" className="text-slate-700 focus:bg-slate-100">This Quarter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Compliance Badges & Controls */}
            <div className="flex items-center gap-3">
              {/* Compliance Badges */}
              <div className="hidden xl:flex items-center gap-2">
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-mono">
                  Schedule-M: ON
                </Badge>
                <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-mono">
                  WHO-GMP: Aligned
                </Badge>
                {isReadOnly && (
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-mono">
                    READ-ONLY MODE
                  </Badge>
                )}
              </div>

              {/* PID Trace Toggle */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200">
                <Scan className={cn("h-4 w-4", pidTraceMode ? "text-cyan-600" : "text-slate-400")} />
                <span className="text-xs font-mono text-slate-500 hidden sm:inline">PID TRACE</span>
                <Switch
                  checked={pidTraceMode}
                  onCheckedChange={toggleTraceMode}
                  className="data-[state=checked]:bg-cyan-500"
                  data-testid="pid-trace-toggle"
                />
              </div>

              {/* Role Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 h-9 px-2 hover:bg-slate-100" data-testid="role-switcher">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-[#fd4816] text-white text-xs">
                        {currentRole.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-700">{currentRole.name.split(' ')[0]}</p>
                      <p className="text-xs text-slate-500">{currentRole.name}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 bg-white border-slate-200">
                  <DropdownMenuLabel className="text-slate-500">Switch Role</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-100" />
                  {roles.map(role => (
                    <DropdownMenuItem
                      key={role.id}
                      onClick={() => switchRole(role.id)}
                      className={cn(
                        "cursor-pointer focus:bg-slate-100",
                        currentRole.id === role.id && "bg-slate-100"
                      )}
                    >
                      <span className="text-slate-700">{role.name}</span>
                      {role.id === 'EXT_AUDITOR' && (
                        <Badge variant="outline" className="ml-auto text-xs border-amber-300 text-amber-600 bg-amber-50">
                          Read-only
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 relative">
          {/* Trace Overlay */}
          {pidTraceMode && <TraceOverlay />}
          
          <Outlet />
        </main>
      </div>

      {/* Trace Inspector Drawer */}
      <TraceInspector />
    </div>
  );
};

export default Shell;
